import { Stack, ResponsiveSection } from '@stitch-fix/mode-react';
import { BusinessLineCarousel, type BusinessLineCarouselProps } from '../BusinessLineCarousel';
import { ContentGallery, type ContentGalleryProps } from '../ContentGallery';
import { Divider, type DividerProps } from '../Divider';
import { Layout, type LayoutProps } from '../Layout';
import { ImageHalfScreenHero, type ImageHalfScreenHeroProps } from '../ImageHalfScreenHero';
import { SEOData, type SEODataProps } from '../SEOData';
import { FAQs, type FAQsProps } from '../FAQs';
import { FooterImageLinks, type FooterImageLinksProps } from '../FooterImageLinks';
import { GetStartedBox, type GetStartedBoxProps } from '../GetStartedBox';
import { ImageContent1Up, type ImageContent1UpProps } from '../ImageContent1Up';
import { LogoGrid, type LogoGridProps } from '../LogoGrid';
import { MerchCarousel, type MerchCarouselProps } from '../MerchCarousel';
import { PromoBanner, type PromoBannerProps } from '../PromoBanner';
import { ReasonsToSignUp, type ReasonsToSignUpProps } from '../ReasonsToSignUp';
import { ShoppableEditorial, type ShoppableEditorialProps } from '../ShoppableEditorial';
import { StylistModule, type StylistModuleProps } from '../StylistModule';
import { TripleBlock, type TripleBlockProps } from '../TripleBlock';
import { TrigallHero, type TrigallHeroProps } from '../TrigallHero/TrigallHero';
import { VideoFullBleedHero, VideoHalfScreenHero, type VideoHalfScreenHeroProps, type VideoFullBleedHeroProps } from '../VideoLayout';
import { ContentBlock, type ContentBlockProps } from '../ContentBlock';
type ModuleProps = {
  type: 'businessLineCarousel';
  props: BusinessLineCarouselProps;
} | {
  type: 'contentBlock';
  props: ContentBlockProps;
} | {
  type: 'contentGallery';
  props: ContentGalleryProps;
} | {
  type: 'divider';
  props: DividerProps;
} | {
  type: 'faqs';
  props: FAQsProps;
} | {
  type: 'footerImageLinks';
  props: FooterImageLinksProps;
} | {
  type: 'getStarted';
  props: GetStartedBoxProps;
} | {
  type: 'imageContent1Up';
  props: ImageContent1UpProps;
} | {
  type: 'imageHalfScreenHero';
  props: ImageHalfScreenHeroProps;
} | {
  type: 'logoGrid';
  props: LogoGridProps;
} | {
  type: 'merchCarousel';
  props: MerchCarouselProps;
} | {
  type: 'promoBanner';
  props: PromoBannerProps;
} | {
  type: 'reasonsToSignUp';
  props: ReasonsToSignUpProps;
} | {
  type: 'shoppableEditorial';
  props: ShoppableEditorialProps;
} | {
  type: 'stylistModule';
  props: StylistModuleProps;
} | {
  type: 'trigallHero';
  props: TrigallHeroProps;
} | {
  type: 'tripleBlock';
  props: TripleBlockProps;
} | {
  type: 'videoFullBleedHero';
  props: VideoFullBleedHeroProps;
} | {
  type: 'videoHalfScreenHero';
  props: VideoHalfScreenHeroProps;
} | {
  type: 'notSupported';
};
export type LandingPageCustomProps = {
  seoData: SEODataProps;
  modules: ModuleProps[];
  viewEventTrackingName?: LayoutProps['viewEventTrackingName'];
};
const moduleResolver = (module: ModuleProps) => {
  if (module.type === 'businessLineCarousel') {
    return <BusinessLineCarousel {...module.props} />;
  }
  if (module.type === 'imageHalfScreenHero') {
    return <ImageHalfScreenHero {...module.props} />;
  }
  if (module.type === 'contentBlock') {
    return <ContentBlock {...module.props} />;
  }
  if (module.type === 'contentGallery') {
    return <ContentGallery {...module.props} />;
  }
  if (module.type === 'divider') {
    return <Divider {...module.props} />;
  }
  if (module.type === 'faqs') {
    return <FAQs {...module.props} />;
  }
  if (module.type === 'footerImageLinks') {
    return <FooterImageLinks {...module.props} />;
  }
  if (module.type === 'getStarted') {
    return <GetStartedBox {...module.props} />;
  }
  if (module.type === 'imageContent1Up') {
    return <ImageContent1Up {...module.props} />;
  }
  if (module.type === 'logoGrid') {
    return <LogoGrid {...module.props} />;
  }
  if (module.type === 'merchCarousel') {
    return <MerchCarousel {...module.props} />;
  }
  if (module.type === 'promoBanner') {
    return <PromoBanner {...module.props} />;
  }
  if (module.type === 'reasonsToSignUp') {
    return <ReasonsToSignUp {...module.props} />;
  }
  if (module.type === 'shoppableEditorial') {
    return <ShoppableEditorial {...module.props} />;
  }
  if (module.type === 'stylistModule') {
    return <StylistModule {...module.props} />;
  }
  if (module.type === 'tripleBlock') {
    return <TripleBlock {...module.props} />;
  }
  if (module.type === 'trigallHero') {
    return <TrigallHero {...module.props} />;
  }
  if (module.type === 'videoFullBleedHero') {
    return <VideoFullBleedHero {...module.props} />;
  }
  if (module.type === 'videoHalfScreenHero') {
    return <VideoHalfScreenHero {...module.props} />;
  }
  return null;
};
export const LandingPageCustom = (props: LandingPageCustomProps) => {
  return <Layout viewEventTrackingName={props.viewEventTrackingName}>
      <ResponsiveSection mt="0" width={{
      sm: 'full-bleed'
    }} gutter="none">
        <SEOData {...props.seoData} />
        <Stack mb={{
        sm: '3rem',
        lg: '4rem'
      }} spacing={{
        sm: '3rem',
        lg: '4rem'
      }}>
          {props.modules.map((module, index) => {
          const key = `${module.type}-${index}`;
          return <div key={key} data-testid={module.type}>
                {moduleResolver(module)}
              </div>;
        })}
        </Stack>
      </ResponsiveSection>
    </Layout>;
};